import { Bell, Layers, List } from 'lucide-react';

import {
  FieldServiceContract,
  FieldServiceEligibilityContract,
  PatchSchedule,
  PostSchedule,
  PostScheduleStoreAssignmentInput,
  PostScheduleTaskInput,
  Product,
  Schedule,
  Store,
  TaskType,
} from '@Models';
import { isKnownTaskType, UPPERCASE_LABEL_CLASS_NAME } from '../../FieldServices/constants';
import {
  ScheduleWizardFirmState,
  ScheduleWizardFormState,
  ScheduleWizardWhatState,
  ScheduleWizardWhereState,
  SelectedStoreKey,
  WizardMonthlyOrdinal,
  WizardStepDefinition,
} from './scheduleWizard.types';

const ALERT_CEILING_HYBRID_MIN = 10;
const ALERT_CEILING_HYBRID_MAX = 90;
const ALERT_CEILING_HYBRID_DEFAULT = 50;

const SECTION_LABEL_CLASS_NAME = UPPERCASE_LABEL_CLASS_NAME;

const DEFAULT_WIZARD_FORM_STATE: ScheduleWizardFormState = {
  name: '',
  where: { selectedStores: [] },
  what: { taskTypeIds: [], categories: [], alertCeilingPct: ALERT_CEILING_HYBRID_DEFAULT, decideWeekly: false },
  frequency: {
    cadence: 'weekly',
    weekdays: [],
    monthlyOrdinal: 'second',
    monthlyWeekday: 0,
    startDate: null,
    endDateMode: 'none',
    endDatePresetWeeks: null,
    endDate: null,
  },
  firm: { mode: 'single', singleFieldServiceId: null, storeAssignments: {} },
};

const END_DATE_PRESET_WEEKS = [2, 4, 10];

const WEEKDAY_REFERENCE_MONDAY_UTC = Date.UTC(2024, 0, 1);
const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface WeekdayOption {
  value: number;
  label: string;
}

const buildWeekdayOptions = (locale: string, weekdayFormat: 'short' | 'long'): WeekdayOption[] => {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: weekdayFormat, timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, dayIndex) => ({
    value: dayIndex,
    label: formatter.format(new Date(WEEKDAY_REFERENCE_MONDAY_UTC + dayIndex * MS_PER_DAY)),
  }));
};

const getWeekdayOptions = (locale: string): WeekdayOption[] => buildWeekdayOptions(locale, 'short');

const getFullWeekdayOptions = (locale: string): WeekdayOption[] => buildWeekdayOptions(locale, 'long');

const getSelectedRetailers = (where: ScheduleWizardWhereState): string[] =>
  Array.from(new Set(where.selectedStores.map((store) => store.retailer)));

const getLockedRetailer = (where: ScheduleWizardWhereState): string | null =>
  where.selectedStores.length > 0 ? where.selectedStores[0].retailer : null;

const isStoreSelectable = (where: ScheduleWizardWhereState, store: Pick<Store, 'retailer'>): boolean => {
  const lockedRetailer = getLockedRetailer(where);
  return lockedRetailer === null || lockedRetailer === store.retailer;
};

const getStoreKey = (entry: Pick<Store, 'retailer' | 'location_code'>): string =>
  `${entry.retailer}::${entry.location_code}`;

interface FieldServiceRetailerEligibility {
  retailer: string;
  contracts: FieldServiceEligibilityContract[];
}

interface FieldServiceCoverage {
  fullyEligibleFieldServiceIds: Set<string>;
  anyCoverageFieldServiceIds: Set<string>;
  coverageByStoreKey: Map<string, Set<string>>;
  uncoveredStoresByFieldService: Map<string, SelectedStoreKey[]>;
}

const EMPTY_FIELD_SERVICE_COVERAGE: FieldServiceCoverage = {
  fullyEligibleFieldServiceIds: new Set(),
  anyCoverageFieldServiceIds: new Set(),
  coverageByStoreKey: new Map(),
  uncoveredStoresByFieldService: new Map(),
};

const computeFieldServiceCoverage = (
  selectedStores: SelectedStoreKey[],
  perRetailerResults: FieldServiceRetailerEligibility[],
): FieldServiceCoverage => {
  if (selectedStores.length === 0) return EMPTY_FIELD_SERVICE_COVERAGE;

  const storesByRetailer = new Map<string, SelectedStoreKey[]>();
  selectedStores.forEach((store) => {
    const list = storesByRetailer.get(store.retailer) ?? [];
    list.push(store);
    storesByRetailer.set(store.retailer, list);
  });

  const coverageByStoreKey = new Map<string, Set<string>>();
  const anyCoverageFieldServiceIds = new Set<string>();
  const fullyCoveredRetailersByFieldService = new Map<string, Set<string>>();

  perRetailerResults.forEach(({ retailer, contracts }) => {
    const requestedKeysForRetailer = (storesByRetailer.get(retailer) ?? []).map(getStoreKey);

    contracts.forEach((contract) => {
      anyCoverageFieldServiceIds.add(contract.field_service_id);
      const coveredKeys = new Set(
        contract.covered_location_codes.map((code) => getStoreKey({ retailer, location_code: code })),
      );
      coveredKeys.forEach((key) => {
        const covering = coverageByStoreKey.get(key) ?? new Set<string>();
        covering.add(contract.field_service_id);
        coverageByStoreKey.set(key, covering);
      });

      const fullyCoversRetailer =
        requestedKeysForRetailer.length > 0 && requestedKeysForRetailer.every((key) => coveredKeys.has(key));
      if (fullyCoversRetailer) {
        const retailers = fullyCoveredRetailersByFieldService.get(contract.field_service_id) ?? new Set<string>();
        retailers.add(retailer);
        fullyCoveredRetailersByFieldService.set(contract.field_service_id, retailers);
      }
    });
  });

  const selectedRetailers = Array.from(storesByRetailer.keys());
  const fullyEligibleFieldServiceIds = new Set(
    Array.from(anyCoverageFieldServiceIds).filter((fieldServiceId) => {
      const fullyCoveredRetailers = fullyCoveredRetailersByFieldService.get(fieldServiceId) ?? new Set<string>();
      return selectedRetailers.every((retailer) => fullyCoveredRetailers.has(retailer));
    }),
  );

  const uncoveredStoresByFieldService = new Map<string, SelectedStoreKey[]>();
  anyCoverageFieldServiceIds.forEach((fieldServiceId) => {
    if (fullyEligibleFieldServiceIds.has(fieldServiceId)) return;
    const uncovered = selectedStores.filter(
      (store) => !coverageByStoreKey.get(getStoreKey(store))?.has(fieldServiceId),
    );
    uncoveredStoresByFieldService.set(fieldServiceId, uncovered);
  });

  return {
    fullyEligibleFieldServiceIds,
    anyCoverageFieldServiceIds,
    coverageByStoreKey,
    uncoveredStoresByFieldService,
  };
};

const EMPTY_FIELD_SERVICE_ID_SET: Set<string> = new Set();

const isFullyEligibleFieldService = (coverage: FieldServiceCoverage, fieldServiceId: string): boolean =>
  coverage.fullyEligibleFieldServiceIds.has(fieldServiceId);

const hasFieldServiceCoverage = (coverage: FieldServiceCoverage, fieldServiceId: string): boolean =>
  coverage.anyCoverageFieldServiceIds.has(fieldServiceId);

const getUncoveredStores = (coverage: FieldServiceCoverage, fieldServiceId: string): SelectedStoreKey[] =>
  coverage.uncoveredStoresByFieldService.get(fieldServiceId) ?? [];

const getCoveringFieldServiceIds = (coverage: FieldServiceCoverage, storeKey: string): Set<string> =>
  coverage.coverageByStoreKey.get(storeKey) ?? EMPTY_FIELD_SERVICE_ID_SET;

const isStoreCoveredByFieldService = (
  coverage: FieldServiceCoverage,
  storeKey: string,
  fieldServiceId: string,
): boolean => getCoveringFieldServiceIds(coverage, storeKey).has(fieldServiceId);

const countFieldServicesWithCoverage = (coverage: FieldServiceCoverage): number =>
  coverage.anyCoverageFieldServiceIds.size;

const countFullyEligibleFieldServices = (coverage: FieldServiceCoverage): number =>
  coverage.fullyEligibleFieldServiceIds.size;

const listFullyEligibleFieldServiceIds = (coverage: FieldServiceCoverage): string[] =>
  Array.from(coverage.fullyEligibleFieldServiceIds);

const getCategoryOptions = (products: Product[], retailers: string[]): string[] => {
  if (retailers.length === 0) return [];
  const distinctCategories = new Set<string>();
  products.forEach((product) => {
    if (retailers.includes(product.retailer) && product.category) distinctCategories.add(product.category);
  });
  return Array.from(distinctCategories).sort();
};

const findContract = (
  retailer: string | null,
  fieldServiceId: string,
  fieldServiceContracts: FieldServiceContract[],
): FieldServiceContract | undefined =>
  fieldServiceContracts.find(
    (contract) => contract.field_service_id === fieldServiceId && contract.retailer === retailer,
  );

const getAssignedFieldServiceId = (
  firm: ScheduleWizardFirmState,
  storeKey: string,
  fieldServiceCoverage: FieldServiceCoverage,
): string | null => {
  if (firm.mode === 'split') return firm.storeAssignments[storeKey] ?? null;
  const { singleFieldServiceId } = firm;
  return singleFieldServiceId && isStoreCoveredByFieldService(fieldServiceCoverage, storeKey, singleFieldServiceId)
    ? singleFieldServiceId
    : null;
};

const buildStoreAssignmentsPayload = (
  formState: ScheduleWizardFormState,
  fieldServiceContracts: FieldServiceContract[],
  fieldServiceCoverage: FieldServiceCoverage,
): PostScheduleStoreAssignmentInput[] =>
  formState.where.selectedStores.map((store) => {
    const fieldServiceId = getAssignedFieldServiceId(formState.firm, getStoreKey(store), fieldServiceCoverage);
    const contract = fieldServiceId ? findContract(store.retailer, fieldServiceId, fieldServiceContracts) : undefined;
    return {
      retailer: store.retailer,
      location_code: store.location_code,
      contract_id: contract?.id ?? null,
    };
  });

const getScheduleFieldServiceIds = (firm: ScheduleWizardFirmState): string[] => {
  if (firm.mode === 'split') return Array.from(new Set(Object.values(firm.storeAssignments)));
  return firm.singleFieldServiceId ? [firm.singleFieldServiceId] : [];
};

const getSharedApprovedTaskTypeCodes = (
  formState: ScheduleWizardFormState,
  fieldServiceContracts: FieldServiceContract[],
): string[] | null => {
  const retailer = getLockedRetailer(formState.where);
  const approvedCodeLists = getScheduleFieldServiceIds(formState.firm).map(
    (fieldServiceId) => findContract(retailer, fieldServiceId, fieldServiceContracts)?.approved_task_types ?? [],
  );
  if (approvedCodeLists.length === 0) return null;
  const [firstCodes, ...otherCodeLists] = approvedCodeLists;
  return firstCodes.filter((code) => otherCodeLists.every((codes) => codes.includes(code)));
};

const hasNoSharedTaskType = (
  formState: ScheduleWizardFormState,
  fieldServiceContracts: FieldServiceContract[],
): boolean =>
  formState.what.alertCeilingPct !== 100 &&
  getSharedApprovedTaskTypeCodes(formState, fieldServiceContracts)?.length === 0;

const buildCategoryRefs = (categories: string[], retailers: string[], products: Product[]) => {
  const refs: { retailer: string; category: string }[] = [];
  categories.forEach((category) => {
    retailers.forEach((retailer) => {
      if (products.some((product) => product.retailer === retailer && product.category === category)) {
        refs.push({ retailer, category });
      }
    });
  });
  return refs;
};

const getTaskTypesMissingCategories = (
  formState: ScheduleWizardFormState,
  taskTypes: TaskType[],
  products: Product[],
): TaskType[] => {
  const { what } = formState;
  if (what.decideWeekly || what.alertCeilingPct === 100 || what.taskTypeIds.length === 0) return [];

  const retailers = getSelectedRetailers(formState.where);
  const categoryRefs = buildCategoryRefs(what.categories, retailers, products);
  const retailersWithAMatch = new Set(categoryRefs.map((ref) => ref.retailer));
  const coversEveryRetailer = retailers.length > 0 && retailers.every((retailer) => retailersWithAMatch.has(retailer));
  if (coversEveryRetailer) return [];

  return what.taskTypeIds
    .map((taskTypeId) => taskTypes.find((taskType) => taskType.id === taskTypeId))
    .filter((taskType): taskType is TaskType => !!taskType && taskType.requires_products);
};

const getTaskPayloadKey = (task: PostScheduleTaskInput): string =>
  [task.task_type_id, ...(task.category_refs ?? []).map((ref) => `${ref.retailer}::${ref.category}`).sort()].join('|');

const buildTasksPayload = (formState: ScheduleWizardFormState, products: Product[]): PostScheduleTaskInput[] => {
  const retailers = getSelectedRetailers(formState.where);
  const categoryRefsPerCategory = formState.what.categories
    .map((category) => buildCategoryRefs([category], retailers, products))
    .filter((categoryRefs) => categoryRefs.length > 0);
  const tasks: PostScheduleTaskInput[] = formState.what.taskTypeIds.flatMap((taskTypeId) =>
    categoryRefsPerCategory.length > 0
      ? categoryRefsPerCategory.map((categoryRefs) => ({ task_type_id: taskTypeId, category_refs: categoryRefs }))
      : [{ task_type_id: taskTypeId }],
  );
  return Array.from(new Map(tasks.map((task) => [getTaskPayloadKey(task), task])).values());
};

const getTaskTypeName = (taskType: TaskType, t: (key: string) => string): string =>
  isKnownTaskType(taskType.code) ? t(`FieldService.TaskTypeName.${taskType.code}`) : taskType.name;

const buildScheduleName = (
  what: ScheduleWizardWhatState,
  taskTypes: TaskType[],
  t: (key: string) => string,
): string => {
  if (what.decideWeekly) return t('Schedule.Wizard.PrioritizeByWeekScheduleName');
  if (what.alertCeilingPct === 100) return t('Schedule.Wizard.AlertsOnlyScheduleName');
  const firstTaskType = taskTypes.find((taskType) => taskType.id === what.taskTypeIds[0]);
  return firstTaskType ? getTaskTypeName(firstTaskType, t) : t('Schedule.Wizard.NewScheduleName');
};

const WEEKS_TO_DAYS = 7;

const getTodayIso = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const addDaysToDateString = (dateString: string, days: number): string => {
  const date = new Date(`${dateString}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const MONTHLY_ORDINAL_CODES: Record<WizardMonthlyOrdinal, string> = {
  first: '1',
  second: '2',
  third: '3',
  fourth: '4',
  last: '-1',
};
const BYDAY_WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

const buildMonthlyByDayToken = (ordinal: WizardMonthlyOrdinal, weekday: number): string =>
  `${MONTHLY_ORDINAL_CODES[ordinal]}${BYDAY_WEEKDAY_CODES[weekday]}`;

const MONTHLY_ORDINAL_OPTIONS: { value: WizardMonthlyOrdinal; labelKey: string }[] = [
  { value: 'first', labelKey: 'Schedule.Wizard.Frequency.MonthlyOrdinalFirst' },
  { value: 'second', labelKey: 'Schedule.Wizard.Frequency.MonthlyOrdinalSecond' },
  { value: 'third', labelKey: 'Schedule.Wizard.Frequency.MonthlyOrdinalThird' },
  { value: 'fourth', labelKey: 'Schedule.Wizard.Frequency.MonthlyOrdinalFourth' },
  { value: 'last', labelKey: 'Schedule.Wizard.Frequency.MonthlyOrdinalLast' },
];

type WorkMixOption = 'alertsOnly' | 'scheduleOnly' | 'both';

const WORK_MIX_OPTIONS: { value: WorkMixOption; labelKey: string; descKey: string; icon: typeof Bell }[] = [
  {
    value: 'alertsOnly',
    labelKey: 'Schedule.Wizard.What.WorkMixAlertsOnly',
    descKey: 'Schedule.Wizard.What.WorkMixAlertsOnlyDesc',
    icon: Bell,
  },
  {
    value: 'scheduleOnly',
    labelKey: 'Schedule.Wizard.What.WorkMixScheduleOnly',
    descKey: 'Schedule.Wizard.What.WorkMixScheduleOnlyDesc',
    icon: List,
  },
  {
    value: 'both',
    labelKey: 'Schedule.Wizard.What.WorkMixBoth',
    descKey: 'Schedule.Wizard.What.WorkMixBothDesc',
    icon: Layers,
  },
];

const getWorkMixOption = (alertCeilingPct: number): WorkMixOption => {
  if (alertCeilingPct === 100) return 'alertsOnly';
  if (alertCeilingPct === 0) return 'scheduleOnly';
  return 'both';
};

const formatMonthlyRecurrence = (
  token: string,
  t: (key: string, options?: Record<string, string>) => string,
  locale: string,
): string | null => {
  const weekday = BYDAY_WEEKDAY_CODES.indexOf(token.slice(-2));
  const ordinal = MONTHLY_ORDINAL_OPTIONS.find((option) => MONTHLY_ORDINAL_CODES[option.value] === token.slice(0, -2));
  if (weekday === -1 || !ordinal) return null;
  return t('Schedule.MonthlyRecurrence', {
    ordinal: t(ordinal.labelKey),
    weekday: getFullWeekdayOptions(locale)[weekday].label,
  });
};

const formatWeekdaysRecurrence = (tokens: string[], locale: string): string | null => {
  const weekdays = tokens.map((token) => BYDAY_WEEKDAY_CODES.indexOf(token));
  if (weekdays.includes(-1)) return null;
  const weekdayOptions = getWeekdayOptions(locale);
  return [...weekdays]
    .sort((a, b) => a - b)
    .map((weekday) => weekdayOptions[weekday].label)
    .join(', ');
};

const formatScheduleRecurrence = (
  schedule: Pick<Schedule, 'cadence' | 'cadence_days'>,
  t: (key: string, options?: Record<string, string>) => string,
  locale: string,
): string => {
  const cadence = t(`Schedule.CadenceLabel.${schedule.cadence}`);
  const days = schedule.cadence_days ?? [];
  const tokens = days.filter((day): day is string => typeof day === 'string');
  if (schedule.cadence === 'once' || tokens.length === 0 || tokens.length !== days.length) return cadence;
  if (schedule.cadence === 'monthly' && tokens.length !== 1) return cadence;
  const recurrence =
    schedule.cadence === 'monthly'
      ? formatMonthlyRecurrence(tokens[0], t, locale)
      : formatWeekdaysRecurrence(tokens, locale);
  return recurrence ? t('Schedule.RecurrenceLabel', { cadence, recurrence }) : cadence;
};

const cadenceUsesWeekdays = (cadence: string): boolean => cadence === 'weekly' || cadence === 'bi_weekly';

const buildCadenceFields = (
  frequency: ScheduleWizardFormState['frequency'],
): Pick<PostSchedule, 'cadence' | 'cadence_days' | 'start_date' | 'end_date'> => {
  const startDate = frequency.startDate as string;
  let endDate: string | null = null;
  if (frequency.endDateMode === 'custom') endDate = frequency.endDate;
  else if (frequency.endDateMode === 'preset' && frequency.endDatePresetWeeks !== null) {
    const anchor = startDate > getTodayIso() ? startDate : getTodayIso();
    endDate = addDaysToDateString(anchor, frequency.endDatePresetWeeks * WEEKS_TO_DAYS);
  }

  if (frequency.cadence === 'once') {
    return { cadence: 'once', cadence_days: null, start_date: startDate, end_date: null };
  }
  if (cadenceUsesWeekdays(frequency.cadence)) {
    return {
      cadence: frequency.cadence,
      cadence_days: frequency.weekdays.map((day) => BYDAY_WEEKDAY_CODES[day]),
      start_date: startDate,
      end_date: endDate,
    };
  }
  return {
    cadence: 'monthly',
    cadence_days: [buildMonthlyByDayToken(frequency.monthlyOrdinal, frequency.monthlyWeekday)],
    start_date: startDate,
    end_date: endDate,
  };
};

const sameCadenceDays = (left: string[], right: (number | string)[] | null): boolean => {
  const normalized = [...(right ?? [])].map(String).sort();
  const sorted = [...left].sort();
  return sorted.length === normalized.length && sorted.every((token, index) => token === normalized[index]);
};

const buildFrequencyStateFromSchedule = (
  schedule: Pick<Schedule, 'cadence' | 'cadence_days' | 'start_date' | 'end_date'>,
): ScheduleWizardFormState['frequency'] | null => {
  const tokens = (schedule.cadence_days ?? []).filter((day): day is string => typeof day === 'string');
  if (schedule.cadence !== 'once' && tokens.length === 0) return null;
  const monthlyToken = schedule.cadence === 'monthly' ? tokens[0] ?? null : null;
  const ordinal = MONTHLY_ORDINAL_OPTIONS.find(
    (option) => monthlyToken !== null && MONTHLY_ORDINAL_CODES[option.value] === monthlyToken.slice(0, -2),
  );
  const monthlyWeekday = monthlyToken === null ? -1 : BYDAY_WEEKDAY_CODES.indexOf(monthlyToken.slice(-2));

  return {
    cadence: schedule.cadence,
    weekdays: cadenceUsesWeekdays(schedule.cadence)
      ? tokens
          .map((token) => BYDAY_WEEKDAY_CODES.indexOf(token))
          .filter((index) => index >= 0)
          .sort((left, right) => left - right)
      : [],
    monthlyOrdinal: ordinal?.value ?? 'first',
    monthlyWeekday: monthlyWeekday >= 0 ? monthlyWeekday : 0,
    startDate: schedule.start_date,
    endDateMode: schedule.end_date === null ? 'none' : 'custom',
    endDatePresetWeeks: null,
    endDate: schedule.end_date,
  };
};

const isFrequencyStateValid = (frequency: ScheduleWizardFormState['frequency']): boolean => {
  if (!frequency.startDate) return false;
  if (frequency.cadence === 'once') return true;
  if (cadenceUsesWeekdays(frequency.cadence) && frequency.weekdays.length === 0) return false;
  if (frequency.endDateMode === 'preset') return frequency.endDatePresetWeeks !== null;
  if (frequency.endDateMode === 'custom') {
    return !!frequency.endDate && frequency.endDate >= frequency.startDate;
  }
  return true;
};

const buildCadenceDiff = (
  schedule: Pick<Schedule, 'cadence' | 'cadence_days' | 'start_date' | 'end_date'>,
  frequency: ScheduleWizardFormState['frequency'],
): PatchSchedule => {
  const next = buildCadenceFields(frequency);
  const nextDays = next.cadence_days ?? [];
  const diff: PatchSchedule = {};

  if (next.cadence !== schedule.cadence) diff.cadence = next.cadence;
  if (!sameCadenceDays(nextDays, schedule.cadence_days)) diff.cadence_days = nextDays;
  if (next.start_date !== schedule.start_date) diff.start_date = next.start_date;
  const staysOnce = next.cadence === 'once' && schedule.cadence === 'once';
  if (!staysOnce && next.end_date !== schedule.end_date) diff.end_date = next.end_date;
  return diff;
};

const buildScheduleCreatePayload = (
  formState: ScheduleWizardFormState,
  products: Product[],
  fieldServiceContracts: FieldServiceContract[],
  fieldServiceCoverage: FieldServiceCoverage,
): PostSchedule => ({
  name: formState.name.trim(),
  source: 'in_app',
  ...buildCadenceFields(formState.frequency),
  alert_ceiling_pct: formState.what.alertCeilingPct,
  store_assignments: buildStoreAssignmentsPayload(formState, fieldServiceContracts, fieldServiceCoverage),
  tasks: buildTasksPayload(formState, products),
});

const WIZARD_STEPS: WizardStepDefinition[] = [
  {
    key: 'where',
    titleKey: 'Schedule.Wizard.Steps.where',
    isValid: (state) => state.where.selectedStores.length > 0,
  },
  {
    key: 'what',
    titleKey: 'Schedule.Wizard.Steps.what',
    isValid: (state) =>
      state.what.alertCeilingPct === 100 || state.what.decideWeekly || state.what.taskTypeIds.length > 0,
  },
  {
    key: 'frequency',
    titleKey: 'Schedule.Wizard.Steps.frequency',
    isValid: (state) => isFrequencyStateValid(state.frequency),
  },
  {
    key: 'firm',
    titleKey: 'Schedule.Wizard.Steps.firm',
    isValid: (state) => {
      const { firm } = state;
      if (state.name.trim() === '') return false;
      if (firm.mode === 'single') return firm.singleFieldServiceId !== null;
      return Object.keys(firm.storeAssignments).length > 0;
    },
  },
];

export {
  addDaysToDateString,
  ALERT_CEILING_HYBRID_DEFAULT,
  ALERT_CEILING_HYBRID_MAX,
  ALERT_CEILING_HYBRID_MIN,
  buildCadenceDiff,
  buildCategoryRefs,
  buildFrequencyStateFromSchedule,
  buildMonthlyByDayToken,
  buildScheduleCreatePayload,
  buildScheduleName,
  cadenceUsesWeekdays,
  computeFieldServiceCoverage,
  countFieldServicesWithCoverage,
  countFullyEligibleFieldServices,
  DEFAULT_WIZARD_FORM_STATE,
  EMPTY_FIELD_SERVICE_COVERAGE,
  END_DATE_PRESET_WEEKS,
  formatScheduleRecurrence,
  getCategoryOptions,
  getCoveringFieldServiceIds,
  getFullWeekdayOptions,
  getLockedRetailer,
  getSelectedRetailers,
  getSharedApprovedTaskTypeCodes,
  getStoreKey,
  getTaskTypeName,
  getTaskTypesMissingCategories,
  getTodayIso,
  getUncoveredStores,
  getWeekdayOptions,
  getWorkMixOption,
  hasFieldServiceCoverage,
  hasNoSharedTaskType,
  isFrequencyStateValid,
  isFullyEligibleFieldService,
  isStoreCoveredByFieldService,
  isStoreSelectable,
  listFullyEligibleFieldServiceIds,
  MONTHLY_ORDINAL_OPTIONS,
  SECTION_LABEL_CLASS_NAME,
  WIZARD_STEPS,
  WORK_MIX_OPTIONS,
};
export type { FieldServiceCoverage, FieldServiceRetailerEligibility, WeekdayOption, WorkMixOption };
